// src/context/TenantContext.js
import { createContext, useContext, useEffect, useState } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../config/firebase";
import { applyBrandPalette } from "../utils/brandPalettes";

const TenantContext = createContext(null);

// Shared with AdminDashboardPage's own (separate) lockout check — public pages
// and the admin dashboard intentionally use independent logic so a change to
// one can never accidentally block the other's very different UI.
function computeBillingLocked(data) {
  const trialEnd = data.trialEndsAt
    ? new Date(data.trialEndsAt)
    : data.trialEnd?.toDate
    ? data.trialEnd.toDate()
    : data.trialEnd
    ? new Date(data.trialEnd)
    : null;
  const trialExpired = data.billingStatus === "trial" && trialEnd && trialEnd <= new Date();
  const isCancelled   = data.billingStatus === "cancelled";
  return trialExpired || isCancelled;
}

export function TenantProvider({ slug, children }) {
  const [tenant,        setTenant]        = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantError,   setTenantError]   = useState("");
  const [refreshKey,    setRefreshKey]    = useState(0);

  useEffect(() => {
    if (!slug) { setTenantLoading(false); return; }

    let cancelled = false;
    async function load() {
      try {
        const q    = query(collection(db, "tenants"), where("slug", "==", slug));
        const snap = await getDocs(q);

        if (cancelled) return;

        const found = snap.docs.find(d => d.data().active !== false);
        if (!found) {
          setTenantError("Business not found.");
        } else if (found.data().suspended) {
          setTenantError("suspended");
        } else {
          const data = { id: found.id, ...found.data() };
          data.billingLocked = computeBillingLocked(data);
          setTenant(data);
          applyBrandPalette(data.brandColor || "green");
        }
      } catch {
        if (!cancelled) setTenantError("Failed to load business.");
      } finally {
        if (!cancelled) setTenantLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [slug, refreshKey]);

  function refreshTenant() { setRefreshKey(k => k + 1); }

  function setTenantWithPalette(data) {
    setTenant(data);
    if (data?.brandColor) applyBrandPalette(data.brandColor);
  }

  return (
    <TenantContext.Provider value={{ tenant, setTenant: setTenantWithPalette, tenantLoading, tenantError, refreshTenant }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
